<template>
  <div class="q-pa-md">
    <transition name="fade-scale">
      <div class="dynamic-island" v-if="selectedContent.length > 0">
        <div class="dynamic-island-content">
          <!-- Center section with total count (moved to left) -->
          <div class="left-section">
            <span class="total-count">{{ validContent.length }} / {{ pagination.total }} files</span>
          </div>

          <!-- Vertical divider -->
          <div class="vertical-divider"></div>

          <!-- Left section with selection count and type chips (moved to center) -->
          <div class="center-section">
            <span class="selection-count-text">{{ selectedContent.length }} selected</span>

            <div class="selection-type-chips">
              <!-- Image type chip with count - future implementation will add more chips -->
              <q-chip icon="o_image" class="content-type-chip image-chip">
                {{ selectedContent.length }}
                <q-tooltip>Selected images</q-tooltip>
              </q-chip>
            </div>
          </div>

          <!-- Vertical divider -->
          <div class="vertical-divider"></div>

          <!-- Right action buttons section -->
          <div class="right-section">
            <q-btn class="action-button" icon="o_file_download" flat dense :disabled="selectedContent.length === 0" @click="handleExport" :loading="isExporting" :percentage="exportProgress">
              <q-tooltip>Download</q-tooltip>
            </q-btn>

            <q-btn
              v-if="selectedCollectionId"
              class="action-button"
              icon="o_delete_outline"
              flat
              dense
              :disabled="selectedContent.length === 0"
              @click="handleRemoveFromCollection"
              :loading="isRemovingFromCollection"
            >
              <q-tooltip>Remove from Collection</q-tooltip>
            </q-btn>

            <q-btn
              v-if="!selectedCollectionId"
              class="action-button"
              icon="o_create_new_folder"
              flat
              dense
              :disabled="selectedContent.length === 0"
              @click="openAddToCollectionDialog"
              :loading="isAddingToCollection"
            >
              <q-tooltip>Add to Collection</q-tooltip>
            </q-btn>

            <q-btn id="more-actions" class="action-button more-actions-btn" icon="o_more_horiz" flat dense :disabled="selectedContent.length === 0">
              <q-tooltip>More Actions</q-tooltip>
              <q-menu anchor="bottom right" self="top right" class="gallery-menu" @show="isMoreActionsMenuOpen = true" @hide="isMoreActionsMenuOpen = false">
                <q-list style="min-width: 120px" class="gallery-menu-list">
                  <q-item clickable v-close-popup @click="handleOpenDeleteConfirmation" class="gallery-menu-item">
                    <q-item-section avatar>
                      <q-icon name="o_delete" color="negative" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="gallery-menu-label warning-label">Delete</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>

            <!-- Vertical divider before close button -->
            <div class="vertical-divider"></div>

            <q-btn class="action-button close-btn" icon="o_close" flat dense @click="deselectAll">
              <q-tooltip>Close</q-tooltip>
            </q-btn>
          </div>
        </div>
      </div>
    </transition>

    <!-- Select All FAB that disappears when all images are selected -->
    <transition name="fade-scale">
      <q-btn
        v-if="selectedContent.length < validContent.length && validContent.length > 0"
        fab
        icon="library_add_check"
        color="grey-3"
        text-color="secondary"
        class="select-all-fab"
        @click="toggleSelectAll"
      >
        <q-tooltip>Select All Images</q-tooltip>
      </q-btn>
    </transition>

    <div class="images-grid">
      <div
        v-for="content in validContent"
        :key="content.id"
        class="grid-item"
        :class="[getImageClass(content), { selected: isSelected(content), 'active-dropdown': activeDropdownImageId === content.id }]"
        @click="toggleSelection(content)"
      >
        <div class="selection-overlay" v-if="isSelected(content)">
          <q-icon name="check_circle" size="32px" color="white" />
        </div>

        <!-- Display videos differently than images -->
        <template v-if="content.type === 'video'">
          <div class="video-container">
            <q-img v-if="content.preview_url" :src="content.preview_url" :placeholder-src="content.placeholder_url" class="grid-image" fit="cover" />
            <div v-else class="grid-image video-placeholder">
              <q-icon name="play_circle_filled" size="48px" color="white" />
            </div>
            <div class="video-duration" v-if="content.duration">
              {{ formatDuration(content.duration) }}
            </div>
          </div>
        </template>
        <q-img v-else :src="content.preview_url" :placeholder-src="content.placeholder_url" class="grid-image" fit="cover" />

        <div class="image-overlay">
          <div class="overlay-info">
            <q-chip icon="o_camera" text-color="grey-6" square size="xs" class="overlay-chip">{{ content.type === 'video' ? 'Video content' : content.shoot_id }}</q-chip>
            <q-chip v-if="content.type === 'video'" icon="o_movie" text-color="grey-6" square size="xs" class="overlay-chip video-chip">
              {{ content.codec || 'Video' }}
            </q-chip>
            <q-chip icon="o_image" text-color="grey-6" v-if="content.width && content.height" square size="xs" class="overlay-chip">
              {{ `${content.width}x${content.height}` }}
            </q-chip>
          </div>
          <div class="action-buttons">
            <q-btn color="black" flat round class="action-btn menu-btn" @click.stop>
              <q-icon name="more_horiz" color="secondary" size="18px" />
              <q-menu
                anchor="bottom right"
                self="top right"
                transition-show="jump-down"
                transition-hide="jump-up"
                :offset="[0, 10]"
                class="gallery-menu"
                @show="activeDropdownImageId = content.id"
                @hide="activeDropdownImageId = null"
              >
                <q-list style="min-width: 120px" class="gallery-menu-list">
                  <q-item clickable v-close-popup @click="handleDownload(content)" :disable="downloadingStates[content.id]" class="gallery-menu-item">
                    <q-item-section avatar>
                      <q-icon name="o_download" :color="downloadingStates[content.id] ? 'grey-6' : 'black'" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="gallery-menu-label custom-label">Download</q-item-label>
                    </q-item-section>
                  </q-item>

                  <q-item clickable v-close-popup @click="handleCopyLink(content.url)" class="gallery-menu-item">
                    <q-item-section avatar>
                      <q-icon name="o_link" color="black" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="gallery-menu-label custom-label">Copy Link</q-item-label>
                    </q-item-section>
                  </q-item>

                  <q-item v-if="content.type === 'video'" clickable v-close-popup @click="playVideo(content)" class="gallery-menu-item">
                    <q-item-section avatar>
                      <q-icon name="o_play_circle" color="black" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="gallery-menu-label custom-label">Play Video</q-item-label>
                    </q-item-section>
                  </q-item>

                  <q-item v-if="role === 'admin' || role === 'client'" clickable v-close-popup @click="shareToMeta(content)" class="gallery-menu-item">
                    <q-item-section avatar>
                      <q-icon name="share" color="black" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="gallery-menu-label custom-label">Share on Meta</q-item-label>
                    </q-item-section>
                  </q-item>

                  <q-item clickable v-close-popup @click="openLightbox(validContent.indexOf(content))" class="gallery-menu-item">
                    <q-item-section avatar>
                      <q-icon name="o_fullscreen" color="black" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="gallery-menu-label custom-label">Open in Lightbox</q-item-label>
                    </q-item-section>
                  </q-item>

                  <q-item v-if="content.type !== 'video'" clickable v-close-popup @click="openEditor(content)" class="gallery-menu-item">
                    <q-item-section avatar>
                      <q-icon name="o_edit" color="black" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="gallery-menu-label custom-label">Edit Image</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>

          <!-- Image name display and edit section at the bottom -->
          <div class="image-name-container" @click.stop>
            <div class="image-name-display" v-if="editingImageId !== content.id">
              <span class="image-name-text">{{ content.name || content.filename }}</span>
              <q-btn flat round dense size="sm" class="edit-name-btn" @click.stop="startEditingName(content)">
                <q-icon name="edit" size="16px" color="white" />
                <q-tooltip>Edit {{ content.type === 'video' ? 'Video' : 'Image' }} Name</q-tooltip>
              </q-btn>
            </div>
            <div class="image-name-edit-wrapper" v-else>
              <input
                ref="nameEditField"
                type="text"
                class="image-name-edit"
                @keydown.enter.prevent="saveImageName(content)"
                @blur="saveImageName(content)"
                :value="content.name || content.filename"
                @click.stop
                spellcheck="false"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Removal Confirmation Dialog -->
    <q-dialog v-model="showRemoveConfirmDialog" transition-show="scale" transition-hide="scale" seamless>
      <q-card class="modal-card">
        <div class="modal-pattern-bg delete-pattern"></div>
        <q-card-section class="modal-header delete-header">
          <q-icon name="remove_circle" color="negative" class="modal-header-icon" />
          <div class="modal-title delete-title">Remove from Collection</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup class="close-btn" />
        </q-card-section>

        <q-separator class="delete-separator" />

        <q-card-section class="modal-body">
          <div class="warning-box">
            <q-icon name="warning" color="negative" class="warning-icon" />
            <div class="warning-text">This will remove the selected content from this collection.</div>
          </div>

          <p class="confirmation-text">Do you want to remove {{ selectedContent.length }} content{{ selectedContent.length !== 1 ? 's' : '' }} from this collection?</p>

          <div class="note-text">
            <q-icon name="info" class="note-icon" />
            <div>This will only remove the content from the collection, not delete them from your library.</div>
          </div>
        </q-card-section>

        <q-card-actions class="modal-actions">
          <q-btn flat no-caps label="Cancel" color="grey-7" v-close-popup class="modal-btn cancel-btn btn-with-transition" />
          <q-btn flat no-caps label="Remove" :loading="isRemovingFromCollection" @click="confirmRemoveFromCollection" class="modal-btn delete-btn btn-with-transition" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <ImageFormatDialog v-if="showExportDialog" v-model="showExportDialog" @format="exportImages" />
    <q-dialog v-if="showShareDialog" v-model="showShareDialog" @hide="selectedShareImage = null" maximized>
      <MetaShareDialog
        :metaIntegration="metaIntegration"
        :mediaUrl="selectedShareImage.url"
        :previewUrl="selectedShareImage.preview_url"
        :placeholderUrl="selectedShareImage.placeholder_url"
        :imageRatio="selectedShareImage.aspect_ratio"
        @hide="selectedShareImage = null"
      />
    </q-dialog>
    <GalleryLightbox v-if="lightboxOpen" :content="validContent" :startIndex="lightboxStartIndex" @close="lightboxOpen = false" />
    <ImagesEditor
      v-if="selectedImageForEditting !== null && showEditorDialog"
      :isOpen="showEditorDialog"
      :image="selectedImageForEditting"
      @update:isOpen="showEditorDialog = $event"
      @close="closeEditor"
    />
    <AddToCollectionDialog
      :show="showAddToCollectionDialog"
      @update:show="showAddToCollectionDialog = $event"
      :selectedContent="selectedContent"
      @add-success="handleAddToCollectionSuccess"
      @loading-state="isAddingToCollection = $event"
    />

    <!-- Delete Confirmation Dialog -->
    <BaseDialog v-model="showDeleteConfirmDialog" title="Delete Content" confirmLabel="Delete" confirmButtonClass="base-dialog__negative-btn" :loading="isDeleting" @confirm="confirmDeleteImages">
      <div class="warning-box">
        <q-icon name="warning" color="negative" class="warning-icon" />
        <div class="warning-text">This will permanently delete the selected content.</div>
      </div>

      <p class="confirmation-text">
        Do you want to delete {{ selectedContent.length }} {{ selectedContent.length === 1 ? (selectedContent[0].type === 'video' ? 'video' : 'image') : 'items' }}? This action cannot be undone.
      </p>
    </BaseDialog>

    <q-dialog v-if="showVideoPlayerDialog" v-model="showVideoPlayerDialog" @hide="selectedVideo = null" maximized>
      <div class="video-player-wrapper">
        <div class="video-player-header">
          <span class="video-title">{{ selectedVideo ? selectedVideo.name || selectedVideo.filename : 'Video Player' }}</span>
          <q-btn icon="close" flat round v-close-popup />
        </div>
        <div class="video-player-content">
          <VideoPlayer v-if="selectedVideo" :src="selectedVideo.video_hls_url || selectedVideo.url" :poster="selectedVideo.preview_url" />
        </div>
      </div>
    </q-dialog>
  </div>
</template>

<script>
import { ContentApi } from '@api/index';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import MetaShareDialog from './meta/MetaShareDialog.vue';
import ImageFormatDialog from './ImageFormatDialog.vue';
import GalleryLightbox from './GalleryLightbox.vue';
import ImagesEditor from './ImagesEditor.vue';
import AddToCollectionDialog from '@components/gallery/AddToCollectionDialog.vue';
import BaseDialog from '@components/base/BaseDialog.vue';
import VideoPlayer from './VideoPlayer.vue';

const ASPECT_RATIO_THRESHOLD = 1.5;
const TALL_RATIO_THRESHOLD = 0.7;

// Helper function to select all text in an element
function selectElementText(el) {
  const range = document.createRange();
  range.selectNodeContents(el);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
}

export default {
  name: 'ImagesGrid',
  components: {
    MetaShareDialog,
    ImageFormatDialog,
    GalleryLightbox,
    ImagesEditor,
    AddToCollectionDialog,
    BaseDialog,
    VideoPlayer
  },
  computed: {
    validContent() {
      return this.content.filter(content => content && content.id);
    },
    showShareDialog: {
      get() {
        return !!this.selectedShareImage;
      },
      set(value) {
        if (!value) {
          this.selectedShareImage = null;
        }
      }
    },
    allSelected: {
      get() {
        return this.selectedContent.length > 0 && this.selectedContent.length === this.content.length;
      },
      set() {
        // The setter is empty because the toggleSelectAll method handles the change
      }
    }
  },
  watch: {
    content: {
      immediate: true,
      handler(newContent) {
        this.images = newContent.filter(item => item && item.id);
      }
    },
    '$route.query.editImage': {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          try {
            const imageData = JSON.parse(newValue);
            console.log('Opening editor', imageData);
            this.selectedImageForEditting = imageData;
            this.showEditorDialog = true;
          } catch (error) {
            console.error('Error parsing image data:', error);
          }
        }
      }
    }
  },

  props: {
    content: {
      type: Array,
      required: true,
      default: () => []
    },
    pagination: {},
    metaIntegration: {
      type: Object,
      required: false,
      default: () => {}
    },
    isMetaConnected: {
      type: Boolean,
      required: false,
      default: false
    },
    visualSearchValue: {
      type: String,
      default: ''
    },
    role: {
      type: String,
      default: '',
      required: true
    },
    selectedCollectionId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      images: [],
      downloadingStates: {},
      selectedContent: [],
      downloadProgress: 0,
      showExportDialog: false,
      isExporting: false,
      exportProgress: 0,
      selectedShareImage: null,
      lightboxOpen: false,
      lightboxStartIndex: 0,
      showEditorDialog: false,
      selectedImageForEditting: null,
      showAddToCollectionDialog: false,
      isAddingToCollection: false,
      isRemovingFromCollection: false,
      showRemoveConfirmDialog: false,
      showDeleteConfirmDialog: false,
      isDeleting: false,
      editingImageId: null,
      activeDropdownImageId: null,
      isMoreActionsMenuOpen: false,
      showVideoPlayerDialog: false,
      selectedVideo: null
    };
  },

  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },

  setup() {
    // Get the query client for cache invalidation
    const queryClient = useQueryClient();

    // Create a mutation for updating content metadata
    const updateContentMetadataMutation = useMutation({
      mutationFn: ({ contentType, id, payload }) => {
        return ContentApi.updateImageMetadata({
          content: contentType,
          id,
          payload
        });
      },
      // Add optimistic update functionality
      onMutate: async variables => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({ queryKey: ['content'] });

        // Snapshot the previous data for potential rollback
        const previousData = queryClient.getQueryData(['content']);

        // Optimistically update the cache
        queryClient.setQueriesData({ queryKey: ['content'] }, oldData => {
          if (!oldData) return oldData;

          // Handle infinite query data structure
          if (oldData.pages) {
            return {
              ...oldData,
              pages: oldData.pages.map(page => ({
                ...page,
                content: page.content.map(item => {
                  // Update the specific content item
                  if (item.id === variables.id) {
                    return { ...item, ...variables.payload };
                  }
                  return item;
                })
              }))
            };
          }

          return oldData;
        });

        // Return context with the snapshot
        return { previousData };
      },
      // If the mutation fails, roll back to the previous data
      onError: (err, variables, context) => {
        if (context?.previousData) {
          queryClient.setQueriesData({ queryKey: ['content'] }, context.previousData);
        }
      },
      onSuccess: () => {
        // No need to invalidate the query on success since we already updated the cache optimistically
      },
      // Always refetch after error or success to ensure cache is in sync with server
      onSettled: () => {
        // Invalidate the query after the mutation completes (success or error)
        // This ensures the cache is eventually consistent with the server
        queryClient.invalidateQueries({ queryKey: ['content'] });
      }
    });

    // Create a mutation for deleting content
    const deleteContentMutation = useMutation({
      mutationFn: async contentItems => {
        const results = { success: 0, failed: 0, deletedIds: [] };

        // Delete each selected content item sequentially
        for (const content of contentItems) {
          try {
            await ContentApi.deleteImage(content.id);
            results.success++;
            results.deletedIds.push(content.id);
          } catch (error) {
            console.error(`Failed to delete ${content.type} with ID ${content.id}:`, error);
            results.failed++;
          }
        }

        return results;
      },
      onSuccess: results => {
        // Invalidate both content and collections queries
        queryClient.invalidateQueries({ queryKey: ['content'] });
        queryClient.invalidateQueries({ queryKey: ['collections'] });

        return results;
      }
    });

    // Create a mutation for removing items from collections
    const removeFromCollectionMutation = useMutation({
      mutationFn: ({ collectionId, contentIds }) => {
        const { CollectionsApi } = require('@api/modules/collections');
        return CollectionsApi.removeItemsFromCollection(collectionId, contentIds);
      },
      onSuccess: () => {
        // Invalidate both content and collections queries
        queryClient.invalidateQueries({ queryKey: ['content'] });
        queryClient.invalidateQueries({ queryKey: ['collections'] });
      }
    });

    // Create a mutation for exporting images
    const exportImagesMutation = useMutation({
      mutationFn: async ({ imageIds, formatOptions }) => {
        // Get the formatted images from the API
        const exportedImages = await ContentApi.exportImages({
          imageIds,
          formatOptions
        });

        return exportedImages;
      }
    });

    return {
      updateContentMetadataMutation,
      deleteContentMutation,
      removeFromCollectionMutation,
      exportImagesMutation
    };
  },

  methods: {
    handleKeyDown(event) {
      if (event.key === 'Escape' && this.selectedContent.length > 0) {
        this.deselectAll();
      }
    },

    isSelected(content) {
      return this.selectedContent.some(selected => selected.id === content.id);
    },

    toggleSelection(content) {
      if (this.isSelected(content)) {
        this.selectedContent = this.selectedContent.filter(selected => selected.id !== content.id);
      } else {
        this.selectedContent.push(content);
      }
    },

    handleExport() {
      if (this.selectedContent.length === 0) return;
      this.showExportDialog = true;
    },

    handleRemoveFromCollection() {
      if (!this.selectedCollectionId || this.selectedContent.length === 0) return;
      // Show confirmation dialog instead of immediately removing
      this.showRemoveConfirmDialog = true;
    },

    async confirmRemoveFromCollection() {
      if (!this.selectedCollectionId || this.selectedContent.length === 0) return;

      this.isRemovingFromCollection = true;

      try {
        // Use the TanStack Query mutation
        await this.removeFromCollectionMutation.mutateAsync({
          collectionId: this.selectedCollectionId,
          contentIds: this.selectedContent.map(item => item.id)
        });

        // Close the dialog
        this.showRemoveConfirmDialog = false;

        // Determine content type for message
        const contentType = this.selectedContent.length === 1 ? (this.selectedContent[0].type === 'video' ? 'video' : 'image') : 'content';

        // Notify success
        this.$q.notify({
          type: 'positive',
          message: `${this.selectedContent.length} ${contentType}${this.selectedContent.length !== 1 ? 's' : ''} removed from collection`,
          position: 'top',
          timeout: 2000
        });

        // Clear selected content
        this.selectedContent = [];

        // Emit event for backward compatibility
        this.$emit('items-removed-from-collection');
      } catch (error) {
        console.error('Error removing items from collection:', error);
        this.$q.notify({
          type: 'negative',
          message: `Failed to remove content: ${error.message}`,
          position: 'top',
          timeout: 2000
        });
      } finally {
        this.isRemovingFromCollection = false;
      }
    },

    async exportImages(formatOptions) {
      if (this.selectedContent.length === 0) return;
      this.showExportDialog = false;
      this.isExporting = true;
      this.exportProgress = 0;

      try {
        // Use the TanStack Query mutation
        const exportedImages = await this.exportImagesMutation.mutateAsync({
          imageIds: this.selectedContent.map(content => content.id),
          formatOptions
        });

        const JSZip = require('jszip');
        const zip = new JSZip();
        const totalImages = exportedImages.length;
        let completedImages = 0;

        await Promise.all(
          exportedImages.map(async image => {
            try {
              const response = await fetch(image.url, { redirect: 'follow' });
              const blob = await response.blob();
              const filename = image.url.split('/').pop().split('?')[0];
              zip.file(filename, blob);
              completedImages++;
              this.exportProgress = Math.round((completedImages / totalImages) * 100);
            } catch (error) {
              console.error(`Error downloading image ${image.id}:`, error);
            }
          })
        );

        const content = await zip.generateAsync({ type: 'blob' });
        const url = window.URL.createObjectURL(content);
        const a = document.createElement('a');
        a.href = url;
        a.download = `formatted-images-${new Date().getTime()}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error('Error in batch format and download:', error);
        this.$q.notify({
          type: 'negative',
          message: `Failed to export content: ${error.message}`,
          position: 'top',
          timeout: 2000
        });
      } finally {
        this.isExporting = false;
        this.exportProgress = 0;
      }
    },

    getImageRatio(content) {
      // If aspect_ratio is directly available, use it
      if (content.aspect_ratio) {
        return content.aspect_ratio;
      }

      // If both width and height are available, calculate ratio
      if (content.width && content.height) {
        return content.width / content.height;
      }

      // For video content with no ratio info, use a default landscape ratio
      if (content.type === 'video') {
        return 16 / 9; // Standard video aspect ratio
      }

      // Default fallback for unrecognized content
      return 1; // Square ratio as default
    },

    getImageClass(content) {
      let gridClass = '';

      // Determine grid size class based on aspect ratio
      if (content.type === 'video') {
        const aspectRatio = this.getImageRatio(content);
        if (aspectRatio > ASPECT_RATIO_THRESHOLD) {
          gridClass = 'grid-item--landscape grid-item--video';
        } else if (aspectRatio < TALL_RATIO_THRESHOLD) {
          gridClass = 'grid-item--portrait grid-item--video';
        } else {
          gridClass = 'grid-item--video';
        }
      } else {
        const aspectRatio = this.getImageRatio(content);
        if (aspectRatio > ASPECT_RATIO_THRESHOLD) {
          gridClass = 'grid-item--landscape';
        } else if (aspectRatio < TALL_RATIO_THRESHOLD) {
          gridClass = 'grid-item--portrait';
        }
      }

      return gridClass;
    },

    async handleDownload(content) {
      this.$set(this.downloadingStates, content.id, true);
      try {
        // Use HLS URL for videos if available, otherwise fallback to regular URL
        const downloadUrl = content.type === 'video' && content.video_hls_url ? content.video_hls_url : content.url;
        const response = await fetch(downloadUrl, { redirect: 'follow' });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = content.url.split('/').pop().split('?')[0];
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error(`Error downloading ${content.type}:`, error);
        this.$q.notify({
          type: 'negative',
          message: `Failed to download ${content.type}. Please try again.`,
          position: 'top',
          timeout: 2000
        });
      } finally {
        this.$set(this.downloadingStates, content.id, false);
      }
    },

    async handleCopyLink(imageUrl) {
      try {
        await navigator.clipboard.writeText(imageUrl);
        this.$q.notify({
          position: 'top',
          type: 'positive',
          message: 'Link copied to clipboard',
          timeout: 2000
        });
      } catch (error) {
        console.error('Error copying to clipboard:', error);
      }
    },

    shareToMeta(image) {
      if (!this.isMetaConnected) {
        this.$router.push('/integrations');
        return;
      }
      this.selectedShareImage = image;
    },

    handleMetaShare() {
      this.$q.notify({
        type: 'positive',
        message: 'Image shared successfully!',
        position: 'top',
        timeout: 2000
      });
    },
    toggleSelectAll() {
      if (this.selectedContent.length === this.validContent.length) {
        // Deselect all
        this.selectedContent = [];
      } else {
        // Select all
        this.selectedContent = [...this.validContent];
      }
    },
    openLightbox(index) {
      this.lightboxStartIndex = index;
      this.lightboxOpen = true;
    },
    openEditor(image) {
      this.selectedImageForEditting = image;
      this.showEditorDialog = true;
    },
    closeEditor() {
      this.showEditorDialog = false;
      this.selectedImageForEditting = null;
    },
    openAddToCollectionDialog() {
      this.showAddToCollectionDialog = true;
    },
    handleAddToCollectionSuccess(message) {
      this.isAddingToCollection = false;
      this.$q.notify({
        type: 'positive',
        message: message,
        position: 'top',
        timeout: 2000
      });
      this.selectedContent = [];
    },
    deselectAll() {
      this.selectedContent = [];
    },
    handleOpenDeleteConfirmation() {
      this.showDeleteConfirmDialog = true;
    },
    async confirmDeleteImages() {
      if (this.selectedContent.length === 0) return;
      this.isDeleting = true;

      try {
        // Use the TanStack Query mutation
        const results = await this.deleteContentMutation.mutateAsync(this.selectedContent);

        // Close the dialog
        this.showDeleteConfirmDialog = false;

        // Determine content type for the notification message
        const contentType = this.selectedContent.length === 1 ? (this.selectedContent[0].type === 'video' ? 'video' : 'image') : 'content';

        const message =
          results.failed === 0
            ? `Successfully deleted ${results.success} ${contentType}${results.success !== 1 ? 's' : ''}`
            : `Deleted ${results.success} item(s), failed to delete ${results.failed} item(s)`;

        const notificationType = results.failed === 0 ? 'positive' : 'warning';

        this.$q.notify({
          type: notificationType,
          message,
          position: 'top',
          timeout: 2000
        });

        // Clear selected content
        this.selectedContent = [];

        // Emit event for backward compatibility
        this.$emit('images-deleted', results.deletedIds);
      } catch (error) {
        console.error('Error deleting content:', error);
        this.$q.notify({
          type: 'negative',
          message: `Failed to delete content: ${error.message}`,
          position: 'top',
          timeout: 2000
        });
      } finally {
        this.isDeleting = false;
      }
    },
    startEditingName(image) {
      this.editingImageId = image.id;
      // Focus on the input field after a small delay to ensure it's rendered
      this.$nextTick(() => {
        const editField = this.$refs.nameEditField;
        if (editField) {
          let targetElement;

          if (Array.isArray(editField)) {
            // If it's an array of refs, select the last one which should be the current one
            targetElement = editField[editField.length - 1];
          } else {
            // If it's a single element
            targetElement = editField;
          }

          if (targetElement) {
            // Focus and select all text in the input field
            targetElement.focus();
            targetElement.select();
          }
        }
      });
    },
    async saveImageName(content) {
      // Get the content from the input field before resetting state
      let newName = '';
      const editField = this.$refs.nameEditField;
      if (editField) {
        let targetElement;

        if (Array.isArray(editField)) {
          targetElement = editField[editField.length - 1];
        } else {
          targetElement = editField;
        }

        if (targetElement) {
          newName = targetElement.value.trim();
        }
      }

      // Store the current editing ID
      const currentEditingId = this.editingImageId;

      // Early returns for invalid cases - clear editing state
      if (!currentEditingId) {
        this.editingImageId = null;
        return;
      }

      // Don't update if name is empty
      if (!newName) {
        this.editingImageId = null;
        return;
      }

      // Don't update if name is unchanged
      if (newName === content.name) {
        this.editingImageId = null;
        return;
      }

      // Immediately clear editing state - the UI will update optimistically
      this.editingImageId = null;

      // Determine content type for messages
      const contentType = content.type === 'video' ? 'video' : 'image';

      try {
        // Use the TanStack Query mutation with optimistic update
        await this.updateContentMetadataMutation.mutateAsync({
          contentType: contentType === 'video' ? 'video' : 'image',
          id: content.id,
          payload: { name: newName }
        });

        this.$q.notify({
          type: 'positive',
          message: `${contentType.charAt(0).toUpperCase() + contentType.slice(1)} name updated`,
          position: 'top',
          timeout: 1500
        });
      } catch (error) {
        // Error handling is done by the mutation's onError
        this.$q.notify({
          type: 'negative',
          message: `Failed to update ${contentType} name`,
          position: 'top',
          timeout: 2000
        });
      }
    },
    formatDuration(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    },

    playVideo(video) {
      this.selectedVideo = video;
      this.showVideoPlayerDialog = true;
    }
  }
};
</script>

<style scoped lang="scss">
.content-wrapper {
  position: relative;
}

.dynamic-island {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--main-bg-color);
  backdrop-filter: blur(10px);
  border-radius: 30px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-width: 95%;
  width: auto;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  padding: 4px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.dynamic-island-content {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  color: var(--main-text-color);
  width: 100%;
  justify-content: space-between;
}

.left-section {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 6px;
  padding-right: 8px;
}

.selection-count-text {
  @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color), $weight: 600);
  margin-right: 4px;
  white-space: nowrap;
}

.center-section {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
}

.total-count {
  @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color), $weight: 600);
  white-space: nowrap;
}

.right-section {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  padding-left: 8px;
  padding-right: 4px;
}

.stats-badge {
  box-shadow: none;
  border-radius: 8px;
  @include inter-font($size: 14px, $bolded: true, $color: var(--terziary-text-color));
  height: 32px;
  padding: 0 16px;
  background-color: rgba(255, 255, 255, 0.1);
  min-width: 0;
  white-space: nowrap;
}

.selection-count {
  background-color: var(--main-btn-color);
  color: var(--main-text-color);
}

.pagination-badge {
  background-color: rgba(255, 255, 255, 0.15);
  white-space: nowrap;
}

.select-all-fab {
  position: fixed;
  right: 24px;
  bottom: 84px;
  z-index: 900;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);

  &:hover {
    transform: scale(1.05);
  }
}

.action-button {
  @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
  margin: 0 4px;
  border-radius: 50%;

  &:hover:not(:disabled) {
    color: var(--secondary-text-color);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* Using ID selector with exact format to disable hover */
#more-actions.q-hoverable:hover > .q-focus-helper {
  opacity: 0 !important;
}

.action-buttons {
  display: flex;
  gap: 6px;
  padding: 8px;
  align-items: flex-start;
  justify-content: flex-end;
  position: absolute;
  top: 0;
  right: 0;
}

.action-btn {
  width: 36px;
  height: 36px;
  background-color: var(--terziary-btn-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--main-text-color);
}

.selection-type-chips {
  display: flex;
  gap: 6px;
  align-items: center;
}

.content-type-chip {
  @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color), $weight: 500);
  padding: 6px 12px;
  height: 28px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.image-chip {
  @include inter-font($size: 14px, $bolded: true, $color: var(--image-chip-text), $weight: 500);
  border: 1px solid var(--image-chip-border);
  background-color: var(--image-chip-bg);
}

.video-chip {
  background-color: rgba(231, 76, 60, 0.15);
  color: var(--negative);
}

.document-chip {
  background-color: rgba(46, 204, 113, 0.15);
  color: var(--positive);
}

/* Restore masonry layout for images grid */
.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(275px, 1fr));
  grid-auto-rows: 275px;
  grid-auto-flow: dense;
  gap: 12px;
  width: 100%;
  max-width: 1800px;
  margin: 0 auto;
  padding-bottom: 100px;
}

.grid-item {
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease;

  &.selected {
    transform: scale(0.98);

    .grid-image {
      filter: brightness(0.7);
    }
  }

  &:hover .image-overlay,
  &.active-dropdown .image-overlay {
    opacity: 1;
    pointer-events: auto;
  }

  &--landscape {
    grid-column: span 2;
    grid-row: span 2;
    @media (max-width: 900px) {
      grid-column: span 1;
      grid-row: span 1;
    }
  }

  &--portrait {
    grid-row: span 2;
  }

  &--video {
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 48px;
      height: 48px;
      background-color: rgba(0, 0, 0, 0.5);
      border-radius: 50%;
      z-index: 1;
    }

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-40%, -50%);
      width: 0;
      height: 0;
      border-top: 10px solid transparent;
      border-bottom: 10px solid transparent;
      border-left: 16px solid white;
      z-index: 1;
    }
  }
}

.selection-overlay {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 2;
  pointer-events: none;
}

.grid-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
  transition: filter 0.3s ease;
  content-visibility: auto;
  contain: content;
  contain-intrinsic-size: 275px;
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
}

.overlay-info {
  display: flex;
  position: absolute;
  top: 8px;
  left: 8px;
}

.overlay-chip {
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 18px;
  font-size: 12px;
  padding: 12px;
  color: #151515 !important;
}

/* Stats badge styles incorporated into chips */

.vertical-divider {
  width: 1px;
  height: 24px;
  background-color: rgba(0, 0, 0, 0.1);
  margin: 0 4px;
}

.close-btn {
  margin-left: 2px;
}

.selection-count {
  padding: 5px 12px;
  min-width: auto;
  width: auto;
  max-width: none;
}

@media (max-width: 600px) {
  .images-grid {
    grid-template-columns: 1fr;
    grid-auto-rows: 200px;
  }
  .grid-image {
    contain-intrinsic-size: 200px;
  }
}

.video-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.video-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #1e1e1e;
}

.video-duration {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 1;
}

.video-player-wrapper {
  background-color: #000;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.video-player-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background-color: #1e1e1e;
  color: white;
}

.video-title {
  font-size: 16px;
  font-weight: 500;
}

.video-player-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Dialog Styles */
.modal-card {
  min-width: 450px;
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
  will-change: transform, opacity;
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}

.modal-pattern-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  background-image: radial-gradient(circle at 10px 10px, rgba(0, 0, 0, 0.03) 1px, transparent 0);
  background-size: 20px 20px;
  z-index: 0;
  opacity: 0.3;
  pointer-events: none;
}

.delete-pattern {
  background-image: radial-gradient(circle at 10px 10px, rgba(244, 67, 54, 0.03) 1px, transparent 0);
}

.modal-header {
  display: flex;
  align-items: center;
  border-radius: 16px 16px 0 0;
  padding: 24px 24px;
  position: relative;
  z-index: 1;
}

.delete-header {
  background: white;
  border-bottom: 1px solid rgba(244, 67, 54, 0.1);
}

.modal-header-icon {
  font-size: 24px;
  margin-right: 12px;
}

.modal-title {
  @include mona-sans-font($size: 20px, $bolded: true, $color: #333);
  letter-spacing: 0.3px;
}

.delete-title {
  color: var(--q-negative);
}

.close-btn {
  opacity: 0.7;
  transition: opacity 0.2s ease, background-color 0.2s ease;

  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.05);
  }
}

.modal-body {
  padding: 28px 24px 16px;
  position: relative;
  z-index: 1;
  background-color: white;
}

.warning-box {
  display: flex;
  align-items: center;
  background-color: rgba(255, 0, 0, 0.04);
  padding: 16px;
  border-radius: 10px;
  border-left: 4px solid #f44336;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.1);
}

.warning-icon {
  font-size: 24px;
  margin-right: 12px;
  color: var(--negative);
}

.warning-text {
  @include mona-sans-font($size: 15px, $bolded: true, $color: var(--danger));
}

.confirmation-text {
  @include inter-font($size: 15px, $color: var(--main-text-color));
  margin: 20px 0;
  line-height: 1.6;
}

.note-text {
  @include inter-font($size: 11px, $bolded: false, $color: var(--secondary-text-color));
  margin-top: 28px;
  display: flex;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.02);
  padding: 10px;
  border-radius: 6px;
}

.note-icon {
  font-size: 14px;
  margin-right: 6px;
  flex-shrink: 0;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px 24px;
  background-color: white;
  position: relative;
  z-index: 1;
}

.modal-btn {
  @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
  height: 44px;
  border-radius: 8px;
  letter-spacing: 0.3px;
  padding: 0 20px;
}

.btn-with-transition {
  transition: background-color 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 1;
  }
}

.cancel-btn {
  margin-right: 12px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

.delete-btn {
  background-color: var(--button-bg-danger);
  color: var(--terziary-text-color);

  &:hover {
    background-color: var(--button-bg-danger);
  }
}

.delete-separator {
  background-color: #ffebee;
  height: 2px;
}

.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: translateX(-50%) scale(0.95) translateY(10px);
}

// Image name editing section
.image-name {
  &-container {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 8px 12px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 70%, transparent 100%);
    pointer-events: auto;
    z-index: 1;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    width: 100%;
    box-sizing: border-box;
  }

  &-display {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: max-content;
    max-width: 100%;
  }

  &-text {
    @include inter-font($size: 24px, $bolded: true, $color: var(--terziary-text-color));
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    text-align: left;
    opacity: 1;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    padding-right: 8px;
    width: max-content;
    max-width: 150px;
  }

  &-edit-wrapper {
    width: 100%;
    padding-right: 8px;
  }

  &-edit {
    @include inter-font($size: 24px, $bolded: true, $color: var(--terziary-text-color));
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
    padding: 4px 8px;
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.3);
    caret-color: white;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: white;
    }
  }
}

// Edit button styling
.edit-name-btn {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
  }
}

@keyframes highlightFade {
  0% {
    background-color: rgba(255, 255, 255, 0.2);
  }
  100% {
    background-color: transparent;
  }
}
</style>

<style lang="scss">
/* Global styles for specific elements */
.custom-label {
  @include inter-font($size: 14px, $bolded: false, $color: var(--main-text-color));
}

.warning-label {
  @include inter-font($size: 14px, $bolded: false, $color: var(--negative));
}

.gallery-menu {
  .q-item {
    &:hover {
      background-color: rgba(0, 0, 0, 0.05) !important;
    }
  }
}

.content-type-chip .q-icon {
  color: var(--image-chip-text);
  font-size: 16px;
  margin-right: 4px;
}
</style>
